import { DateTime } from 'luxon';

import {
    Segment,
    SegmentDefinition,
    SegmentEngagement,
    SegmentFilterState,
    SegmentFilterDemographicFields,
    SegmentFilterEngagementFields,
    SegmentFilterEventFields,
} from 'domains/segments/types';
import { getDemographicFilter, getEngagementFilter, getEventFilter } from 'domains/segments/utils';
import { CurationSegments, FilterCategory } from 'models/enums';
import { formatPhoneNumber } from 'utils';

import { Detail } from './components/CampaignDetails/CampaignDetails';
import { getFormattedParentAudienceDelayInterval } from './pages/CurationsPage/CampaignDetailsPage/timeFormatting';
import { SplitNodesState } from './state/conditionalSplit/splitNodes/splitNodesSlice';
import {
    DelayFields,
    Send,
    SendRecurrenceFrequency,
    CampaignChannels,
    ABTestVariant,
    EmailSendInputState,
    SplitTypes,
} from './types';

/**
 * Removes blank time-delay-holder sends (no content configured yet, no splitType) created by
 * addBranchSend when the user picks "time delay" then "conditional split" without configuring
 * content. Runs on the post-getSendsForApi shape, where content lives in a different field per
 * channel (templateId for Email, smsContentId for SMS, contentId for Direct Mail), so "has
 * content" must check whichever field applies to the send.
 * Transfers their parentAudienceDelayInterval to children and updates all parentIndex /
 * segment parentSendIndex references so the publish payload is structurally valid.
 */
export const squashBlankTimedelaySends = (sends: any[]): any[] => {
    const hasContent = (send: any): boolean =>
        !!send.templateId || !!send.smsContentId || !!send.contentId || !!send.content?.id;
    const blankInfo = new Map<number, { parentIndex: number | null; delay?: string }>();
    sends.forEach((send, i) => {
        if (!hasContent(send) && !send.splitType && send.parentIndex != null) {
            blankInfo.set(i, { parentIndex: send.parentIndex, delay: send.parentAudienceDelayInterval });
        }
    });
    if (blankInfo.size === 0) return sends;

    let removedSoFar = 0;
    const newIndexOf: (number | null)[] = sends.map((_, i) => {
        if (blankInfo.has(i)) {
            removedSoFar++;
            return null;
        }
        return i - removedSoFar;
    });

    const resolveAncestor = (idx: number | null): { index: number | null; delay?: string } => {
        let delay: string | undefined;
        while (idx != null && blankInfo.has(idx)) {
            const info = blankInfo.get(idx)!;
            if (!delay && info.delay) delay = info.delay;
            idx = info.parentIndex;
        }
        return { index: idx != null ? newIndexOf[idx] : null, delay };
    };

    return sends
        .filter((_, i) => !blankInfo.has(i))
        .map((send) => {
            let s = { ...send };
            if (s.parentIndex != null) {
                const { index, delay } = resolveAncestor(s.parentIndex);
                s.parentIndex = index;
                if (!s.parentAudienceDelayInterval && delay) s.parentAudienceDelayInterval = delay;
            }
            if (s.segments?.length) {
                s.segments = s.segments.map((seg: any) => {
                    if (seg.parentSendIndex == null) return seg;
                    const { index } = resolveAncestor(blankInfo.has(seg.parentSendIndex) ? seg.parentSendIndex : null);
                    const remapped = blankInfo.has(seg.parentSendIndex) ? index : newIndexOf[seg.parentSendIndex];
                    return { ...seg, parentSendIndex: remapped };
                });
            }
            return s;
        });
};

/**
 * Resolves the campaign-level exit-criteria segment definition for the publish/update payload.
 * Returns the snapshotted definition, or null when no exit criteria is configured so the field
 * is explicitly cleared when none.
 */
export const getExitSegmentDefinition = (campaignState: {
    exitCriteria?: SegmentDefinition | null;
}): SegmentDefinition | null => campaignState.exitCriteria ?? null;

/**
 * Builds the campaign-wide sends list fed to a SegmentDefinition criteria builder's engagement send
 * picker (exit criteria, conditional split). Unsaved sends get a negative sentinel id of
 * -(fullIndex+1) — the send's position in the full campaignState.sends array — which the backend
 * resolves to the real send id at publish. Covers the whole campaign, unfiltered by position, so a
 * conditional split can reference sends anywhere in the tree, including its own to-be-created
 * MEETS/DOES_NOT_MEET branch sends. Includes sends of every channel — each carries its own
 * `channel` so callers can filter the picker down to the channel of the activity being built.
 */
export const getCampaignSendsForCriteriaBuilder = (
    sends: { channel: CampaignChannels; [key: string]: any }[]
): Send[] =>
    sends.map((send, fullIndex) => {
        const existingId = send.id;
        return ({
            id: existingId ?? -(fullIndex + 1),
            name: send.name || '',
            status: send.status || 'DRAFT',
            channel: send.channel,
            // Lets ValueAutocomplete extract clickable links from the template HTML directly for
            // an unsaved (negative sentinel id) EMAIL send, since it can't be looked up by id yet.
            // Only meaningful for EMAIL — other channels don't store content under `templateId`.
            emailTemplateId: send.channel === CampaignChannels.EMAIL ? send.templateId : undefined,
        } as unknown) as Send;
    });

/**
 * Formats SMS sender profile countryCode and number to +1 (999) 999-9999.
 */
export const getFormattedSmsSenderNumber = (
    smsSenderProfile:
        | {
              countryCode?: string;
              number?: string;
          }
        | null
        | undefined
): string => {
    if (!smsSenderProfile?.countryCode && !smsSenderProfile?.number) return '';
    const raw = (smsSenderProfile?.countryCode ?? '') + (smsSenderProfile?.number ?? '');
    const digits = raw.replace(/\D/g, '');
    const toFormat = digits.length === 10 ? '1' + digits : digits;
    return formatPhoneNumber(toFormat);
};

export const getSendDatetimeError = (datetime: DateTime, isDate: boolean) => {
    if (!datetime?.isValid) {
        return `Please enter a valid ${isDate ? 'date' : 'time'}.`;
    }

    if (!(datetime <= DateTime.now().plus({ month: 6 }))) {
        return `Please enter a ${isDate ? 'date' : 'time'} within the next 6 months.`;
    }

    if (!(datetime >= DateTime.now().minus({ hour: 1 }))) {
        return 'A Curation cannot be scheduled in the past.';
    }
};

export const getEndDateTimeError = (endDatetime: DateTime, sendDatetime: DateTime) => {
    if (!endDatetime?.isValid) {
        return 'Please enter a valid date and time.';
    }

    if (!(endDatetime >= sendDatetime)) {
        return 'A Curation cannot end before the send date.';
    }
};

export const minDate = () => DateTime.now().minus({ hour: 1 });
export const maxDate = () => DateTime.now().plus({ month: 6 });

export const minTime = (sendDatetime: string) =>
    DateTime.fromISO(sendDatetime).hasSame(minDate(), 'day') ? minDate() : null;
export const maxTime = (sendDatetime: string) =>
    DateTime.fromISO(sendDatetime).hasSame(maxDate(), 'day') ? DateTime.now() : null;

export const getRecurrenceLabel = (
    recurrenceFrequency: SendRecurrenceFrequency,
    sendDatetime?: string,
    recurrenceDayOfWeek?: string,
    recurrenceOccurrencePosition?: number
) => {
    if (recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS) {
        return `continual`;
    }
    if (recurrenceFrequency === SendRecurrenceFrequency.EVERY_WEEKDAY) {
        return `${recurrenceFrequency} (Monday - Friday)`;
    }
    if (recurrenceFrequency === SendRecurrenceFrequency.WEEKLY && sendDatetime) {
        return `weekly on ${DateTime.fromISO(sendDatetime).toFormat('cccc')}`;
    }
    if (recurrenceFrequency === SendRecurrenceFrequency.MONTHLY) {
        const ordinalMap: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', [-1]: 'last' };
        const dayMap: Record<string, string> = {
            MO: 'monday',
            TU: 'tuesday',
            WE: 'wednesday',
            TH: 'thursday',
            FR: 'friday',
            SA: 'saturday',
            SU: 'sunday',
        };
        const ordinal = recurrenceOccurrencePosition != null ? ordinalMap[recurrenceOccurrencePosition] : undefined;
        const day = recurrenceDayOfWeek ? dayMap[recurrenceDayOfWeek] : undefined;
        if (ordinal && day) {
            return `monthly on ${ordinal} ${day}`;
        }
        return 'monthly';
    }
    return recurrenceFrequency ?? '';
};

export const getIsRecurrenceContinuous = (recurrenceFrequency: SendRecurrenceFrequency) =>
    recurrenceFrequency === SendRecurrenceFrequency.CONTINUOUS;

export const getIsRecurrenceEnabled = (recurrenceFrequency: SendRecurrenceFrequency) =>
    Boolean(recurrenceFrequency) && recurrenceFrequency !== SendRecurrenceFrequency.DOES_NOT_REPEAT;

type GetCriteriaDetailProps = {
    contentId?: number;
    delayFields?: DelayFields; // pass for sends before save
    delayInterval?: string; // pass for saved sends
    firstEmailRecurrenceFrequency?: SendRecurrenceFrequency;
    scheduledTimestamp?: string;
};
const getCriteriaDetail = ({
    contentId,
    delayFields,
    delayInterval,
    firstEmailRecurrenceFrequency,
    scheduledTimestamp,
}: GetCriteriaDetailProps) => {
    const isEmail = !!contentId;
    if (isEmail) {
        const parentAudienceDelayInterval =
            delayInterval ?? getFormattedParentAudienceDelayInterval(delayFields?.delay, delayFields?.unit);

        return {
            contentNodeId: `${contentId}`,
            contentId,
            firstEmailRecurrenceFrequency,
            parentAudienceDelayInterval,
            scheduledTimestamp,
        };
    }

    return { description: 'exit curation' };
};

type SendWithDelayFields = Partial<Send> & {
    delayFields?: DelayFields;
};
type GetConditionalSplitCurationDetailsProps = {
    firstSend: SendWithDelayFields;
    noCriteriaSend: SendWithDelayFields;
    segments: Segment[];
    yesCriteriaSend: SendWithDelayFields;
};
export const getConditionalSplitCurationDetails = ({
    firstSend,
    noCriteriaSend,
    segments,
    yesCriteriaSend,
}: GetConditionalSplitCurationDetailsProps): Detail[] => {
    const isSegmentsDidOpen =
        // This check is for campaigns before save
        segments[0]?.name === 'opened_sends' ||
        // This check is for saved campaigns
        // (1st segment is user-generated, 2nd/3rd segment is system-generated based on conditional split criteria)
        segments[1]?.segmentDefinition?.engagement[0]?.name === 'opened_sends';
    const splitDescription = `${isSegmentsDidOpen ? CurationSegments.DID_OPEN : CurationSegments.DID_CLICK} email`;

    const initialSendDetail = {
        contentNodeId: `${firstSend.id}`,
        contentId: firstSend.id,
        recurrenceFrequency: firstSend.recurrenceFrequency,
        scheduledEndTimestamp: firstSend.scheduledEndTimestamp,
        scheduledTimestamp: firstSend.scheduledTimestamp,
    };
    const splitDetail = { header: 'Conditional Split', description: splitDescription };
    const meetsCriteriaDetail = getCriteriaDetail({
        contentId: yesCriteriaSend?.id,
        delayFields: yesCriteriaSend?.delayFields,
        delayInterval: yesCriteriaSend?.parentAudienceDelayInterval,
        firstEmailRecurrenceFrequency: firstSend.recurrenceFrequency,
        scheduledTimestamp: yesCriteriaSend?.scheduledTimestamp,
    });
    const doesNotMeetCriteriaDetail = getCriteriaDetail({
        contentId: noCriteriaSend?.id,
        delayFields: noCriteriaSend?.delayFields,
        delayInterval: noCriteriaSend?.parentAudienceDelayInterval,
        firstEmailRecurrenceFrequency: firstSend.recurrenceFrequency,
        scheduledTimestamp: noCriteriaSend?.scheduledTimestamp,
    });

    return [
        initialSendDetail,
        splitDetail,
        {
            header: 'Meets Criteria',
            ...meetsCriteriaDetail,
        },
        {
            header: 'Does Not Meet Criteria',
            ...doesNotMeetCriteriaDetail,
        },
    ];
};

/**
 * Prepares the multi-split publish payload.
 * For each send that carries a `splitNodeId`, resolves the segment criteria from
 * the corresponding `splitNodes` entry and strips the frontend-only `splitNodeId`.
 * The MEETS send gets the node's `segmentDefinition` as-is; the DOES_NOT_MEET send
 * gets the same definition with `negateQuery: true` so it matches everyone else.
 * Sends without a `splitNodeId`, or whose split node has no `segmentDefinition` yet,
 * are passed through unchanged.
 */
export const resolveConditionalSplitSends = (
    sends: EmailSendInputState[],
    splitNodes: SplitNodesState
): Omit<EmailSendInputState, 'splitNodeId'>[] =>
    sends.map((send) => {
        const { splitNodeId, ...rest } = send;
        if (!splitNodeId) return rest;
        const node = splitNodes[splitNodeId];
        if (!node || !node.segmentDefinition) return rest;
        const segmentDefinition =
            send.splitType === SplitTypes.DOES_NOT_MEET
                ? { ...node.segmentDefinition, negateQuery: !node.segmentDefinition.negateQuery }
                : node.segmentDefinition;
        return { ...rest, segmentDefinition };
    });

/**
 * Convert segmentDefinition to segments array for UI
 */
export const convertSegmentDefinitionToSegments = (
    segmentDefinition?: SegmentDefinition,
    sends: any[] = []
): SegmentFilterState<SegmentFilterDemographicFields | SegmentFilterEngagementFields | SegmentFilterEventFields>[] => {
    if (!segmentDefinition) {
        return [];
    }

    const segments: SegmentFilterState<any>[] = [];

    if (segmentDefinition.demographic && segmentDefinition.demographic.length > 0) {
        segmentDefinition.demographic.forEach((demographic, index) => {
            segments.push(getDemographicFilter(demographic, index));
        });
    }

    if (segmentDefinition.engagement && segmentDefinition.engagement.length > 0) {
        segmentDefinition.engagement.forEach((engagement, index) => {
            segments.push(getEngagementFilter(engagement, segments.length + index, sends as Send[]));
        });
    }
    if (segmentDefinition.contactEvent && segmentDefinition.contactEvent.length > 0) {
        segmentDefinition.contactEvent.forEach((event, index) => {
            segments.push(getEventFilter(event, segments.length + index));
        });
    }
    if (segmentDefinition.contactObject && segmentDefinition.contactObject.length > 0) {
        segmentDefinition.contactObject.forEach((event, index) => {
            segments.push(getEventFilter(event, segments.length + index, FilterCategory.CONTACT_OBJECTS));
        });
    }

    return segments;
};

/**
 * Transform draft send from API to CampaignSend format
 */
export const transformDraftSendFromApi = (apiSend: any, allSends: any[] = []) => {
    // Convert NOOP to EMAIL for UI, preserve split criteria
    if (apiSend.channel === CampaignChannels.NOOP) {
        const transformedSend: any = {
            ...apiSend,
            channel: CampaignChannels.EMAIL,
        };

        if (apiSend.segmentDefinition) {
            transformedSend.segments = convertSegmentDefinitionToSegments(apiSend.segmentDefinition, allSends);
        } else if (apiSend.segments && apiSend.segments.length > 0) {
            transformedSend.segments = apiSend.segments.map((engagement: SegmentEngagement, index: number) =>
                getEngagementFilter(engagement, index, allSends as Send[])
            );
        }

        return transformedSend;
    }

    if (apiSend.channel === CampaignChannels.EMAIL) {
        const transformedSend: any = {
            ...apiSend,
            subjectLine: apiSend.subjectLine || '',
            preHeader: apiSend.preHeader || '',
        };

        if (apiSend.segmentDefinition) {
            transformedSend.segments = convertSegmentDefinitionToSegments(apiSend.segmentDefinition, allSends);
        } else if (apiSend.segments && apiSend.segments.length > 0) {
            transformedSend.segments = apiSend.segments.map((engagement: SegmentEngagement, index: number) =>
                getEngagementFilter(engagement, index, allSends as Send[])
            );
        }

        return transformedSend;
    }

    if (apiSend.channel === CampaignChannels.DIRECT_MAIL) {
        if (apiSend.contentId) {
            const { contentId, contentName, ...rest } = apiSend;
            return {
                ...rest,
                content: {
                    id: contentId,
                    name: contentName || '',
                },
            };
        }
        // Ensure content property exists even when contentId is not present
        // This matches the structure expected by DirectMailContentStep component
        // and prevents "Cannot read properties of undefined (reading 'id')" errors
        return {
            ...apiSend,
            content: {
                id: null,
                name: '',
            },
        };
    }

    if (apiSend.channel === CampaignChannels.SMS) {
        // Normalize SMS sender profile: API may return smsSenderProfileId or senderProfileId
        const smsSenderProfileIdFromApi = apiSend.smsSenderProfileId ?? apiSend.senderProfileId;

        if (apiSend.smsContentId) {
            const { smsContentId, contentName, ...rest } = apiSend;
            return {
                ...rest,
                ...(smsSenderProfileIdFromApi != null && { smsSenderProfileId: smsSenderProfileIdFromApi }),
                content: {
                    id: smsContentId,
                    name: contentName || '',
                },
            };
        }
        // Ensure content property exists even when smsContentId is not present
        // This matches the structure expected by SmsContentStep component
        // and prevents "Cannot read properties of undefined (reading 'id')" errors
        return {
            ...apiSend,
            ...(smsSenderProfileIdFromApi != null && { smsSenderProfileId: smsSenderProfileIdFromApi }),
            content: {
                id: null,
                name: '',
            },
        };
    }

    if (apiSend.channel === CampaignChannels.VOICE_CALL) {
        return apiSend;
    }

    return apiSend;
};

/**
 * Calculate which fields are different between two email sends for A/B testing
 * @param sendA - First email send
 * @param sendB - Second email send
 * @returns Array of ABTestVariant indicating which fields are different
 */
export const calculateComparisonFields = (sendA: EmailSendInputState, sendB: EmailSendInputState): ABTestVariant[] => {
    const differences: ABTestVariant[] = [];

    // Compare subject line
    if (sendA.subjectLine !== sendB.subjectLine) {
        differences.push(ABTestVariant.SUBJECT_LINE);
    }

    // Compare preview text (preHeader)
    if (sendA.preHeader !== sendB.preHeader) {
        differences.push(ABTestVariant.PREVIEW_TEXT);
    }

    // Compare content (templateId)
    if (sendA.templateId !== sendB.templateId) {
        differences.push(ABTestVariant.BODY);
    }

    // Compare sender profile
    if (sendA.senderProfileId !== sendB.senderProfileId) {
        differences.push(ABTestVariant.SENDER_PROFILE);
    }

    // Compare schedule timestamp
    if (sendA.scheduledTimestamp !== sendB.scheduledTimestamp) {
        differences.push(ABTestVariant.SCHEDULE_TIME);
    }

    return differences;
};
